<template>
    <div class="page page-product-detail">
        <div v-if="productData" class="container">
            <DelayHydration>
                <Breadcrumb :items="breadcrumbs" :product-name="productData.name" :last-hide="productData.status === 1" />
            </DelayHydration>

            <div class="service-detail__main">
                <ProductDetailSidebarHead
                    v-if="!$device.isDesktop"
                    :product-data="productData"
                    :product-title-suffix="productTitleSuffix"
                    class="sidebarhead-mobile"
                />

                <div ref="carouselWrap" class="service-detail__main__left">
                    <DelayHydration>
                        <ProductDetailCarousel
                            :id="productData.id"
                            :discount-percent="productData.percentDiscount || 0"
                            :name="productData.name"
                            :images="productData.images"
                            :images-overviews="productData.imagesOverviews"
                            :attributes="productData.attributes"
                            :price-per-piece="productData.pricePerPiece || 0"
                            :product="productData"
                        />
                    </DelayHydration>

                    <component :is="productData.desktopBannerUrl ? 'a' : 'div'" :href="productData.desktopBannerUrl">
                        <img
                            v-if="productData.desktopBanner"
                            ref="banner"
                            :src="productData.desktopBanner"
                            loading="lazy"
                            class="d-none d-md-block"
                        />
                    </component>

                    <!--                    <BenefitBoxes v-if="!isUserAuthenticated" class="benefitBoxes--detail" />-->
                </div>

                <div ref="stickySidebarWrap" class="service-detail__main__right">
                    <NuxtjsStickySidebar
                        class="stickySidebarWrap"
                        :top-spacing="135"
                        :bottom-spacing="20"
                        container-selector=".service-detail__main__right"
                        inner-wrapper-selector=".service-detail__main__right__wrapinner"
                        resize-sensor="0"
                        :min-width="991"
                    >
                        <div ref="stickySidebar" class="service-detail__main__right__wrapinner">
                            <div class="service-detail__main__right__inner">
                                <ProductDetailSidebarHead
                                    v-if="$device.isDesktop"
                                    :product-data="productData"
                                    :product-title-suffix="productTitleSuffix"
                                    :is-small="true"
                                    class="sidebarhead-desktop"
                                />

                                <TruncatedText
                                    class="service-detail__shortdescription"
                                    :text="productData.content.shortNoFullHtml"
                                    :limit="80"
                                    :limit-desktop="80"
                                />

                                <GiftEvents
                                    v-if="topGiftEvent"
                                    :gift-actions="[topGiftEvent]"
                                    :product-id="productData.id"
                                    :refresh-discount-price="!$device.isDesktop"
                                    orange-background
                                    class="mt-2 mb-3 d-md-none"
                                />

                                <VariantsContainer :product="productData" />

                                <template v-if="productData.productMonthlyPackages.length">
                                    <h2 class="monthly-packages__title">{{ $t(`productDetail.monthlyPackages.title`) }}</h2>
                                    <div class="monthly-packages">
                                        <div
                                            v-for="productMonthlyPackage in productData.productMonthlyPackages"
                                            :key="productMonthlyPackage.numberOfMonths"
                                            class="monthly-packages__item"
                                            @click="addToBasket(productMonthlyPackage.quantity)"
                                        >
                                            <div>
                                                <div>
                                                    {{
                                                        $t(
                                                            `productDetail.monthlyPackages.monthlyPackage${productMonthlyPackage.numberOfMonths}.title`
                                                        )
                                                    }}
                                                    <span class="color-grey"
                                                        >({{ `${productMonthlyPackage.quantity} ${$t('pieces')}` }})</span
                                                    >
                                                </div>
                                                <div v-if="productMonthlyPackage.hasFreeDelivery">
                                                    <span class="monthly-packages__visual-tag">{{
                                                        $t(`productDetail.monthlyPackages.freeDelivery`)
                                                    }}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <s
                                                    v-if="
                                                        productMonthlyPackage.originalTotalPrice >
                                                        productMonthlyPackage.discountedTotalPrice
                                                    "
                                                    >{{ $formatCurrency(productMonthlyPackage.originalTotalPrice) }}</s
                                                >
                                                <strong>{{ $formatCurrency(productMonthlyPackage.discountedTotalPrice) }}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <LayoutProductDetailAvailabilityAndPricing :product-data="productData" />

                                <BenefitPaymentWarning v-if="!productData.benefitPaymethodEnabled" />

                                <GiftEvents
                                    v-if="bottomGiftEvents.length"
                                    :gift-actions="bottomGiftEvents"
                                    :product-id="productData.id"
                                    :allow-countdown-in-gift-events="topGiftEvent ? !topGiftEvent.showCountdown : false"
                                />

                                <UspList
                                    v-if="productData.status === 1"
                                    :show-free-shipping-in-app="productData.showFreeShippingInAppUSP"
                                />

                                <WatchdogProduct v-if="watchdogEnabled && productData.status !== 1" :product-data="productData" />
                            </div>

                            <LayoutProductDetailTrustReview
                                v-if="productData.giftActions?.length === 0"
                                :product-data="productData.productRating"
                            />

                            <AdditionalProducts
                                v-if="productData.additionalProducts.length !== 0"
                                :products="productData.additionalProducts"
                            />
                        </div>
                    </NuxtjsStickySidebar>
                </div>

                <div ref="tabsSpecification" class="service-detail__content mb-0" :style="`min-height: ${minHeightContent}px;`">
                    <div v-if="visualTags && visualTags.length" class="service-detail__badges">
                        <div class="row">
                            <div class="col">
                                <div class="product-badges">
                                    <div
                                        v-for="(item, index) in visualTags"
                                        :key="index"
                                        :badge-id="item.id"
                                        class="item"
                                        :style="{ 'background-color': item.backgroundColor, color: item.textColor }"
                                    >
                                        <img
                                            v-if="item.image"
                                            v-lazy-load
                                            src="/nuxt-assets/images/lazy-load-error.svg"
                                            :data-src="item.image"
                                            :alt="item.name"
                                            width="16"
                                        />
                                        {{ item.name }}
                                    </div>
                                    <a
                                        v-for="(item, index) in productData.productCertificates"
                                        :key="index"
                                        :href="item.shopUrlPath"
                                        :badge-id="item.id"
                                        class="item certificates"
                                    >
                                        <img
                                            v-lazy-load
                                            src="/nuxt-assets/images/lazy-load-error.svg"
                                            :data-src="item.image"
                                            :alt="item.name"
                                            width="16"
                                        />
                                        <span> {{ item.name }} </span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <QuantityDiscount
                        v-if="productData.productMonthlyPackages.length <= 1"
                        :quantity-groups="productData.amountPrices"
                        :product="productData"
                    />

                    <template v-if="showBundles">
                        <DynamicBundles :product="(productData as unknown) as Product" :bundles="addonSaleBundles" />
                    </template>

                    <div v-if="productData.availabilityPeriod > 1 || productData.status !== 1" class="mt-3">
                        <CarouselBox
                            :products="similarProducts"
                            :title="$t('productDetail.tabs.similarTitleNotInStock')"
                            :type-item="'slim-carousel__item--addon'"
                            class="carouselBox--fullWidth carouselBox--dealDetail"
                            :sticker-title="similarProducts[0]?.availabilityText"
                        />
                        <SkeletonProductCarouselSkeleton v-if="!similarProductsLoaded" :max-number-of-products="4" />
                    </div>

                    <RecommendedProducts v-if="productData.substitutions?.products?.length" :substitutions="productData.substitutions" />

                    <template v-if="productData.service && productData.service.trustBadge">
                        <TrustBadge :data="productData.service.trustBadge" :benefits="productData.service.benefits" />
                    </template>

                    <div ref="ratingBox"></div>

                    <template v-if="productData.comments.total > 0">
                        <div v-if="showRatingWasAddedFlashMessage" class="flash-message flash-message--success mb-4" role="alert">
                            <img
                                v-lazy-load
                                src="/nuxt-assets/images/lazy-load-error.svg"
                                data-src="/nuxt-assets/images/icons/check-green.svg"
                                class="me-2"
                            />
                            {{ $t('serviceDetail.ratingWasAdded') }}
                        </div>
                    </template>

                    <div v-if="productData.availabilityPeriod > 1 || productData.status !== 1" class="service-detail__sliders">
                        <CarouselBox
                            v-for="(carousel, index) in addonSaleAreas"
                            :key="index"
                            :title="carousel.title"
                            :products="carousel.products"
                            :type-item="'slim-carousel__item--addon'"
                            class="carouselBox--fullWidth carouselBox--dealDetail"
                        />
                    </div>

                    <DelayHydration>
                        <ProductDetailSpecification
                            id="tabsHeader"
                            :product-data="productData"
                            :product-tab-links="productTabLinks"
                            :product-id="productData.id"
                            :description="productData.content.longNoFullHtml"
                            :comments-count="productData.comments.commentCount || productData.comments.total"
                            :review-list-data="reviews"
                            :active-tab="activeTab"
                            :discussions-count="discussionsCount"
                        />
                    </DelayHydration>
                </div>

                <div v-if="productData.status === 1" class="service-detail__sliders">
                    <DelayHydration v-if="productData.amountInStock === 0">
                        <SimilarProducts
                            :product-id="productData.id"
                            :product-url="productData.url"
                            :amount-in-stock="productData.amountInStock"
                        />
                    </DelayHydration>

                    <!--                    <div v-if="productDetailCarouselAbTestVariant === 0" id="zoe-product-detail-recco-carousel"></div>-->

                    <!--                    v-else-if="productDetailCarouselAbTestVariant === 1 || productDetailCarouselAbTestVariant === null"-->
                    <lazy-load-component root-margin="600px 0px 600px 0px">
                        <CarouselById
                            v-if="alternativeProductIds.length"
                            :ids="alternativeProductIds"
                            :title="$t('productDetail.recommendationCarouselTitle')"
                            :products-in-skeleton="6"
                            :item-list-name="productDetailRecommendationProducts"
                            :available-only="true"
                            :in-stock-only="true"
                            :luigis-box-carousel-type="getLuigisBoxCarouselType('RecoDetailComplementsAbTest') || 'item_detail'"
                            :luigis-box-item-ids="productData.url"
                            :is-luigis-box-variant="isLuigisBoxCarouselVariant('RecoDetailComplementsAbTest')"
                            :preserve-ids-order="true"
                            class-name="carouselBox--fullWidth"
                        />
                    </lazy-load-component>

                    <DelayHydration>
                        <ProductAddonSaleAreas :product-id="productData.id" />
                    </DelayHydration>

                    <DelayHydration v-if="productData.amountInStock > 0">
                        <SimilarProducts
                            :product-id="productData.id"
                            :product-url="productData.url"
                            :amount-in-stock="productData.amountInStock"
                        />
                    </DelayHydration>
                </div>
            </div>
        </div>

        <Modal
            ref="modal"
            modal-name="modal-form-review"
            size="lg"
            modal-class="modal-form-review"
            :title="$t('productDetail.writeRating')"
            :backdrop-close="false"
        >
            <FormAddReview
                :object-id="productData.id"
                :type="commentTypeProduct"
                :default-name="userName"
                :default-email="userEmail"
                :question-ratings="productData.comments.questionRatings"
                :service-name="productData.name"
                :service-office-name="''"
                :feedback-hash="feedbackUser ? feedbackUser.feedbackHash : ''"
                :applied="''"
            />
        </Modal>

        <!-- removed due to legal issues - IS-6888 -->
        <!--        <ProductSubstitutions v-if="false" :substitutions="productData.substitutions" :current-product="(productData as unknown) as Product" />-->

        <ModalAdult v-if="productData && productData.attributes.isForAdultsOnly" />
    </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import Breadcrumb from '../../navigations/Breadcrumb.vue'
import ProductDetailCarousel from '../../layout/productDetail/Carousel.vue'
import TrustBadge from '../../elements/TrustBadge.vue'
import ProductDetailSpecification from '../../layout/productDetail/Specification.vue'
import { useMatomoViewportTrack, useMatomoClickTrack } from '~/composables/useMatomo'
import CommentType from '~/value/CommentType'
import FormAddReview from '~/components/elements/FormAddReview.vue'
import VariantsContainer from '~/components/elements/VariantsContainer.vue'
import TruncatedText from '~/components/elements/TruncatedText.vue'
import RecommendedProducts from '~/components/layout/productDetail/RecommendedProducts.vue'
import ProductDetailSidebarHead from '~/components/layout/productDetail/ProductDetailSidebarHead.vue'
import UspList from '~/components/objects/UspList.vue'
import CarouselBox from '~/components/elements/CarouselBox.vue'
import QuantityDiscount from '~/components/elements/QuantityDiscount.vue'
import ModalAdult from '~/components/objects/ModalAdult.vue'
import Modal from '~/components/Modal.vue'
import SimilarProducts from '~/components/layout/productDetail/SimilarProducts.vue'
import ProductAddonSaleAreas from '~/components/layout/productDetail/ProductAddonSaleAreas.vue'
import AdditionalProducts from '~/components/layout/productDetail/AdditionalProducts.vue'
import GiftEvents from '~/components/layout/productDetail/GiftEvents.vue'
import FullProduct from '~/models/Product/FullProduct'
import ProductTabLinks from '~/models/Product/ProductTabLinks'
import BreadcrumbItem from '~/models/BreadcrumbItem'
import { useBasketStore } from '~/stores/basket'
import Cashback from '~/components/ui/Cashback.vue'
import ProductSubstitutions from '~/components/objects/ProductSubstitutions.vue'
import DynamicBundles from '~/components/layout/productDetail/DynamicBundles.vue'
import ProductVisualTag from '~/models/Product/ProductVisualTag'
import { useAbTestStore } from '~/stores/abTest'
import BenefitPaymentWarning from '~/components/elements/BenefitPaymentWarning.vue'
import AddonSaleBundle from '~/models/Product/AddonSale/AddonSaleBundle'
import NuxtjsStickySidebar from '~/components/layout/productDetail/NuxtjsStickySidebar.vue'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import CarouselById from '~/components/elements/CarouselById.vue'
import Product from '~/models/Product/Product'
import Review from '~/models/ReviewClub/Review'
import { useAuthStore } from '~/stores/auth'
import { addProductToBasket, useCookie } from '#imports'
import WatchDog from '~/components/objects/Discussion/Tools/WatchDog.vue'
import WatchdogProduct from '~/components/layout/productDetail/WatchdogProduct.vue'
import ProductGiftAction from '~/models/GiftEvent/ProductGiftAction'
import LuigisRecoTypes from '~/value/LuigisRecoTypes'

export default defineNuxtComponent({
    components: {
        WatchdogProduct,
        WatchDog,
        CarouselById,
        BenefitPaymentWarning,
        DynamicBundles,
        ProductSubstitutions,
        Cashback,
        SimilarProducts,
        VariantsContainer,
        FormAddReview,
        Breadcrumb,
        ProductDetailCarousel,
        TrustBadge,
        ProductDetailSpecification,
        TruncatedText,
        RecommendedProducts,
        ProductDetailSidebarHead,
        CarouselBox,
        QuantityDiscount,
        ModalAdult,
        Modal,
        NuxtjsStickySidebar,
        ProductAddonSaleAreas,
        AdditionalProducts,
        GiftEvents,
        UspList,
    },

    props: {
        productTabLinks: {
            type: Object as PropType<ProductTabLinks>,
            required: true,
        },
        productData: {
            type: Object as PropType<FullProduct>,
            required: true,
        },
        breadcrumbs: {
            type: Array as PropType<BreadcrumbItem[]>,
            required: true,
        },
        reviews: {
            type: Array as PropType<Review[]>,
            required: true,
        },
        feedbackUser: {
            type: Object,
        },
        productTitleSuffix: {
            type: String,
        },
        addonSaleBundles: {
            type: Array as PropType<AddonSaleBundle[]>,
            required: true,
        },
        discussionsCount: {
            type: Number,
            required: true,
        },
    },

    data() {
        const basketStore = useBasketStore()

        return {
            activeTab: 'tab-all',
            showRatingWasAddedFlashMessage: false as boolean,
            modalDeliveryVisible: false,
            minHeightContent: 0,
            similarProducts: [] as Product[],
            similarProductsLoaded: false,
            addonSaleAreas: [] as { title: string; products: Product[] }[],
            isDynamicBundleAbTestZoeVariant: false,
            productDetailCarouselAbTestVariant: null,
            hasProductInBasket: basketStore.hasProduct(this.productData.id),
            alternativeProductIds: [] as number[],
        }
    },

    computed: {
        visualTags() {
            return this.productData.visualTags.merged.filter(
                (visualTag: ProductVisualTag) => visualTag.showOnProductDetail && !visualTag.showAsImageOnly
            )
        },

        commentTypeProduct(): number {
            return CommentType.PRODUCT
        },

        authUser(): object | null {
            const basketStore = useBasketStore()
            return basketStore.authUser
        },

        userName(): string {
            if (this.authUser) {
                return `${this.authUser.firstName} ${this.authUser.lastName}`
            } else if (Object.keys(this.feedbackUser).length && this.feedbackUser.feedbackHash !== '') {
                return `${this.feedbackUser.user.firstName} ${this.feedbackUser.user.lastName}`
            } else {
                return ''
            }
        },

        userEmail(): string {
            if (this.authUser) {
                return this.authUser.email
            } else if (Object.keys(this.feedbackUser).length && this.feedbackUser.feedbackHash !== '') {
                return this.feedbackUser.user.email
            } else {
                return ''
            }
        },

        productDetailRecommendationProducts(): string {
            return GtmItemListNameEnum.ProductDetailRecommendationProducts
        },

        showBundles(): boolean {
            return this.productData.status === 1 && this.productData.availabilityPeriod < 48 && !this.hasProductInBasket
        },

        watchdogEnabled(): boolean {
            const config = useRuntimeConfig()

            return config.public.watchdogEnabled === true
        },

        topGiftEvent(): ProductGiftAction | null {
            return this.productData.giftActions?.length ? this.productData.giftActions.slice(0, 1)[0] : null
        },

        bottomGiftEvents(): ProductGiftAction[] {
            return this.productData.giftActions?.length >= 2 ? this.productData.giftActions.slice(1) : []
        },
    },

    created() {
        this.activeTab = this.getActiveTabFromRoute()
    },

    async mounted() {
        const abTestStore = useAbTestStore()
        if (abTestStore.getVariant('DynamicBundleAbTestV2') !== null) {
            const actualVariant = abTestStore.getVariant('DynamicBundleAbTestV2')
            // New version of dynamic bundle AB test is running
            this.isDynamicBundleAbTestZoeVariant = actualVariant !== null && [0, 1].includes(actualVariant)
        } else {
            const actualVariant = abTestStore.getVariant('DynamicBundleAbTest')
            this.isDynamicBundleAbTestZoeVariant = actualVariant !== null && [0].includes(actualVariant)
        }

        this.productDetailCarouselAbTestVariant = abTestStore.getVariant('ProductDetailCarouselAbTest')

        document.body.classList.add('body-product-detail')

        useNuxtApp().$bus.$on('setActiveTab', (arg: string) => {
            this.setActiveTabAndScroll(arg, false)
        })

        useNuxtApp().$bus.$off('showReviewDetail')
        useNuxtApp().$bus.$on('showReviewDetail', (reviewId: number) => {
            safeNavigateTo(this.getTabRoute('tab-reviews') + '?reviewId=' + reviewId)
        })

        useNuxtApp().$bus.$off('commentSaved')
        useNuxtApp().$bus.$on('commentSaved', () => {
            this.$refs.modal?.classList.remove('show')
            this.showRatingWasAddedFlashMessage = true
            window.scrollTo(0, this.$refs.ratingBox.offsetTop - 150)
        })

        this.activeTab = this.getActiveTabFromRoute()

        const lastViewProductsIds = useCookie<number[]>('lastViewProductsIds').value

        if (lastViewProductsIds !== undefined) {
            if (!lastViewProductsIds.includes(this.productData.id)) {
                lastViewProductsIds.splice(49)
                lastViewProductsIds.push(this.productData.id)
                useCookie<number[]>('lastViewProductsIds').value = lastViewProductsIds
            }
        } else {
            const products = [this.productData.id]
            useCookie('lastViewProductsIds').value = JSON.stringify(products)
        }

        // @ts-expect-error
        window.richContentController?.()

        window.addEventListener('scroll', this.handleScroll)

        if (useRoute().hash === '#recommend') {
            this.setActiveTabAndScroll('tab-reviews', true)
        }

        this.minHeightContent = this.$refs.stickySidebar.clientHeight - this.$refs.carouselWrap.clientHeight

        if (this.productData.availabilityPeriod > 1 || this.productData.status !== 1) {
            this.addonSaleAreas = await this.$repository.getProductAddonSaleAreas(this.productData.id)
            const productSimilarProductsResponse = await this.$repository.getProductSimilarProducts(this.productData.id, 1)
            this.similarProducts = productSimilarProductsResponse.products
            this.similarProductsLoaded = true
        }

        try {
            const rateBought = parseInt(useRoute()?.query['rate-bought'] as string)
            if (rateBought === 1) {
                this.$bus.$emit('showModal', 'modal-form-review')
            }
        } catch (e) {}

        const bannerElement = this.$refs.banner
        if (bannerElement) {
            useMatomoViewportTrack(bannerElement, {
                category: 'Product Detail Banner',
                action: 'view',
                name: 'Product Detail Banner',
                value: this.productData.productBannerId,
            })

            useMatomoClickTrack(bannerElement, {
                category: 'Product Detail Banner',
                action: 'click',
                name: 'Product Detail Banner',
                value: this.productData.productBannerId,
            })
        }

        if (abTestStore.getVariant('RecoDetailComplementsAbTest') === 0) {
            const response = await this.$repository.getRecoProducts(
                LuigisRecoTypes.ITEM_DETAIL_COMPLEMENTS,
                this.productData.id,
                useUidCookie().value,
                useCookie('_lb').value ?? ''
            )
            this.alternativeProductIds = response.products.map((product: Product) => product.id)
        } else {
            this.alternativeProductIds = this.productData.attributes.machineLearningAddOnProducts.filter(
                (productId: number) => productId !== this.productData.id
            )
        }
    },

    unmounted() {
        document.body.classList.remove('body-product-detail')
        window.removeEventListener('scroll', this.handleScroll)
    },

    methods: {
        getLuigisBoxCarouselType(testName: string) {
            if (!this.isLuigisBoxCarouselVariant(testName)) {
                return null
            }

            if (testName === 'RecoDetailComplementsAbTest') {
                return LuigisRecoTypes.ITEM_DETAIL_COMPLEMENTS
            }

            return null
        },

        isLuigisBoxCarouselVariant(testName: string) {
            return useAbTestStore().getVariant(testName) === 0
        },

        getTabRoute(tabName: string) {
            return this.productTabLinks[tabName] || this.productData.url
        },

        showWatchdogModal() {
            useNuxtApp().$bus.$emit('showModal', 'watchdog-product-modal')
        },

        setActiveTabAndScroll(arg: string, onlyTabs: boolean) {
            this.activeTab = arg
            const tabsHeaderElement = document.getElementById('tabsHeader')
            if (!tabsHeaderElement) {
                return
            }

            const tabsPosition = tabsHeaderElement.offsetTop
            const pagePosition = window.pageYOffset
            const heightWindow = window.innerHeight
            const overPosition = (heightWindow / 3) * 2 + pagePosition
            const idealPosition = tabsPosition - heightWindow / 4

            if (tabsPosition > overPosition || onlyTabs) {
                window.scrollTo(0, idealPosition)
            }
        },

        getActiveTabFromRoute() {
            const route = useRoute()
            const path = route.fullPath.split('?')[0]
            const tabNamesByLinks = Object.fromEntries(Object.entries(this.productTabLinks).map((tab) => tab.reverse()))
            return tabNamesByLinks[path] || 'tab-all'
        },

        handleScroll() {
            if (window.innerWidth > 992) {
                this.positionSidebar()
            }
        },

        openDeliveryModal(): void {
            if (!this.productData.availabilityModalEnabled) {
                return
            }

            useNuxtApp().$bus.$emit('showModal', 'modal-deliveries')
            this.modalDeliveryVisible = true
        },

        positionSidebar() {
            const sideBarWrap = this.$refs.stickySidebarWrap
            const sideBar = this.$refs.stickySidebar
            const heightWindow = window.innerHeight
            const sideBarHeight = sideBar.clientHeight

            if (heightWindow > sideBarHeight) {
                sideBarWrap.classList.add('onlySticky')
            } else {
                sideBarWrap.classList.remove('onlySticky')
            }
        },

        async getDiscussionsCount() {
            let posts = []
            try {
                const authStore = useAuthStore()
                const accessToken: string | null = authStore.accessToken

                posts = await this.$repository.getSelectedDiscussionsList(this.productId, null, { accessToken })
            } catch {}

            return posts.length
        },

        addToBasket(amount: number) {
            addProductToBasket({
                fullProduct: this.productData,
                productId: this.productData.id,
                amount,
                showPreBasket: true,
            })
        },
    },

    provide() {
        return {
            productIdReferrer: computed(() => this.productData?.id),
        }
    },
})
</script>

<style lang="scss">
@import '../../../node_modules/bootstrap/scss/functions';
@import '../../../node_modules/bootstrap/scss/variables';
@import '../../../node_modules/bootstrap/scss/mixins';

@import '../../../assets/scss/tools/mixins';

.flash-message {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    border-radius: 5px;
    padding: 0.7rem 1rem;
    font-weight: 500;

    &--success {
        background-color: $color-green-light;
        color: $color-green-dark;
    }
}

.service-detail {
    &__main {
        background: $color-white;
        margin-top: 1em;
        margin-bottom: 2em;

        @include media-breakpoint-up(lg) {
            display: grid;
            grid-gap: 1.5em;
            gap: 1.5em;
            grid-template-columns: minmax(10px, 1fr) 400px;
            grid-template-areas:
                'images info'
                'content info';
        }
    }

    &__main__left {
        @include media-breakpoint-up(lg) {
            grid-area: images;
            padding: 0;
            text-align: center;
        }
    }

    &__main__right {
        @include media-breakpoint-up(lg) {
            grid-area: info;
            grid-row: 1 / 3;
            position: relative;
            margin-top: -30px;
        }

        @include media-breakpoint-down(md) {
            &.sticky {
                position: static;
            }
        }
    }

    &__main__right__wrapinner {
        z-index: 96;
        transition: top 0.5s;
    }

    &__shortdescription {
        flex: 1 1 auto;
        margin-bottom: 0;
        line-height: 20px;
        font-size: 16px;
        display: inline-block;
    }

    &__fulldesc {
        font-size: 14px;
        margin-bottom: 1.5em;
        line-height: 20px;
    }

    &__main-image {
        align-self: center;
        text-align: center;
    }

    &__big-image {
        display: flex;
        position: relative;
        width: 100%;

        &-gallery {
            width: calc(100% - 140px);

            @include media-breakpoint-down(md) {
                width: 100%;
                padding: 0 65px;
            }
        }

        .carousel {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .carousel-control-prev,
        .carousel-control-next {
            &-icon {
                width: 45px;
                height: 45px;
            }
        }
    }

    &__bottom {
        grid-column-start: 1;
        grid-column-end: 3;
    }

    &__other-images {
        display: flex;
        flex-direction: column;
        grid-gap: 12px;
        gap: 12px;
        overflow-x: auto;
        padding-bottom: 0.5rem;
        flex: 0 0 125px;
        min-width: 125px;

        @include media-breakpoint-down(sm) {
            display: none;
        }

        &__image {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 80px;
            width: 100px;
            height: 80px;
            object-fit: contain;
            cursor: pointer;
            border: 2px solid $color-grey-dark;
            border-radius: 10px;
            padding: 0.5em;

            &.active {
                border-color: $color-purple;
            }
        }

        img {
            max-height: 60px;
            max-width: 60px;
        }
    }

    &__title {
        font-size: 1.5em;
        width: calc(100% - 40px);
        line-height: 1.2;
        font-weight: 500;
        margin-top: -8px;
        margin-bottom: 8px;
    }

    &__basket-box {
        &__price .alert-addcart {
            display: none;
        }

        @media (max-width: 480px) {
            &__price {
                position: fixed;
                bottom: 72px;
                left: 0;
                width: 100%;
                z-index: 100;
                background-color: $color-white;
                padding: 0 1em 1.5em 1em;
                font-size: 13px;
                box-shadow: 0px -4px 4px -2px rgba(0, 0, 0, 0.068);
            }

            a.delivery-term {
                display: none;
            }
        }

        @include screen-sm {
            &__price {
                font-size: 14px;
            }
            &__price .alert-addcart {
                display: block;
                text-align: center;
            }
        }

        &__button {
            display: flex;
            flex-direction: column;
        }

        .product-buttons {
            display: flex;
            width: 100%;
            justify-content: flex-end;

            @include screen-md {
                display: block;
            }

            .click-and-collect_container {
                display: none;
            }

            @include media-breakpoint-up(md) {
                &.grid {
                    display: grid;
                    grid-gap: 10px;
                    grid-template-columns: 1fr 1fr;

                    .click-and-collect_container {
                        display: block;
                    }
                }
            }
        }
    }

    &__content {
        grid-area: content;
        margin-bottom: 2em;
        @include box;
        padding: 0 0 1rem 0;
        transition: height 5s ease;

        table {
            width: 100% !important;
            border-collapse: collapse;
            border: none;

            tr {
                border-bottom: 1px solid #dee2e6;

                &:hover td {
                    @media (min-width: 1024px) {
                        background-color: $color-grey;
                    }
                }
            }

            td {
                padding: 0.5rem 0.5rem;
                font-size: 14px;
            }
        }

        .offices {
            max-width: 790px;
            margin: 0 auto;
            border-radius: 5px;
        }

        &__card {
            &__image {
                border-radius: 5px;
                max-height: 160px;
            }

            .thumb {
                float: left;
                margin-right: 1em;

                @include media-breakpoint-down(md) {
                    width: 70px;
                }
            }

            .title {
                overflow: hidden;
            }

            .desc {
                overflow: hidden;

                @include media-breakpoint-down(md) {
                    margin-top: 1.5em;
                    clear: both;
                }
            }
        }
    }

    &__sliders {
        margin-top: 0.5rem;
    }
}

.benefits {
    &__item {
        position: relative;
        display: flex;
        align-items: center;
        border-radius: 5px;
        background-color: $color-grey;
        height: 40px;
        font-size: 0.9rem;
        margin-bottom: 0.5em;
        margin-left: 20px;
        padding: 0 0.75em 0 2em;
        line-height: 17px;
        font-weight: 500;

        &::before {
            content: '';
            position: absolute;
            left: -20px;
            top: 0;
            width: 40px;
            height: 40px;
            border: 3px solid $color-white;
            border-radius: 50px;
            background: $color-grey url('/nuxt-assets/icons/like.svg') no-repeat center center;
        }

        &__tooltip {
            float: right;
            width: 20px;
            height: 20px;
            cursor: pointer;
            background: url('/nuxt-assets/icons/info.svg') no-repeat center center;
            margin-left: auto;
        }
    }
}

.service-tabs {
    padding: 0;
    display: flex;

    .nav-item {
        border-bottom: 1px solid #ccc;

        &:hover,
        &.active {
            border-bottom: 1px solid pink;
        }
    }

    .nav-link {
        padding: 0;
        border: 0;
        background: transparent;
        font-size: 16px;
    }
}

.modal {
    .modal-content {
        font-size: 14px;
        box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 20%);
        border: none;
        padding: 0 1rem 1rem 1rem;
    }

    .modal-header {
        border-bottom: 0;
        padding: 0;
        margin-bottom: 1rem;
        font-weight: 500;

        .modal-title {
            padding-top: 1rem;
        }

        .close {
            font-size: 0;
            width: 45px;
            height: 45px;
            position: relative;
            background-color: transparent;
            border: none;
            margin-right: -1rem;

            &::after {
                content: '';
                left: 0;
                top: 0;
                width: 45px;
                height: 45px;
                position: absolute;
                background: url('/nuxt-assets/icons/close.svg') no-repeat 50%;
            }
        }
    }

    .modal-body {
        padding: 0;
    }
}

.modal-max {
    .carousel-control-prev-icon,
    .carousel-control-next-icon {
        width: 65px;
        height: 65px;
        position: absolute;
        z-index: 999;
    }

    .carousel-control-prev-icon {
        left: 0;
    }

    .carousel-control-next-icon {
        right: 0;
    }

    .carousel-control-prev,
    .carousel-control-next {
        font-size: 0;
        width: 50px;
        opacity: 1;
    }

    .carousel-inner,
    .carousel-item {
        height: 100% !important;
    }

    .carousel {
        height: auto;
        max-height: 100%;
    }

    .modal-dialog {
        height: calc(100% - 3.5rem);

        @include media-breakpoint-up(sm) {
            max-width: calc(100vw - 80px);
        }
    }

    .modal-content {
        box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 20%);
        border: none;
        max-height: 100%;
        height: 100%;
    }

    .modal-body {
        flex-direction: column;
        display: flex;
        justify-content: center;
        height: calc(100% - 45px);
    }

    @include media-breakpoint-up(md) {
        .modal-xl {
            align-items: stretch;
        }
    }

    .modal-image {
        padding: 0 1rem 30px 1rem;
        height: calc(100% - 45px);

        .carousel-control-prev-icon,
        .carousel-control-next-icon {
            width: 50px;
            height: 50px;
        }

        .carousel {
            width: 100%;
        }

        img {
            max-height: 100%;
        }
    }

    .modal-caption {
        text-align: center;
        color: $color-grey-extra-dark;
        font-size: 1rem;
        margin: 0.5em 0;
        font-weight: 500;
    }
}

.amount {
    display: flex;
    align-items: center;

    &__incr,
    &__decr {
        background-color: $color-cta;
        color: $color-white;
        width: 50px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 3px;
        font-size: 18px;
        cursor: pointer;
        font-weight: 500;

        &.disabled {
            color: $color-grey-extra-dark;
            background: $color-grey-semi-dark;
            opacity: 0.6;
            cursor: default;
        }
    }

    &__input {
        max-width: 100px;
        height: 40px;
        padding: 5px;
        text-align: center;
        border: none;
    }

    &__unit {
        padding: 0 0.5rem;
    }
}

.closest-office {
    &-desc {
        font-size: 14px;
        color: $color-grey-extra-dark;
        padding: 0 1em;

        .name,
        .title {
            color: $color-dark;
        }

        .distance {
            white-space: nowrap;
            color: $color-dark;
        }
    }

    hr {
        height: 1px;
        border: none;
        background-color: #94aab3;
    }
}

.service-detail__big-image .carousel-control-prev,
.service-detail__big-image .carousel-control-next {
    opacity: 1;
}

.product-detail__main__left--small {
    #carousel {
        width: 50%;
        margin: auto;
        opacity: 0.5;
        filter: grayscale(1);
    }
    .service-detail__big-image {
        min-height: 200px;
    }
    .carousel-container {
        height: 250px;
        margin: 0;
    }
    .carousel-container .main-image,
    .carousel-container .carousel-item {
        max-height: 250px;
    }
}
.page-product-detail {
    .price {
        font-weight: 700;
        color: $color-cta;

        @media (min-width: 480px) {
            font-size: 28px;
        }
    }

    .price-before {
        font-size: 1.2em;

        @media (min-width: 480px) {
            font-size: 18px;
        }
    }

    .price {
        font-size: 1.2em;

        @media (min-width: 480px) {
            font-size: 26px;
        }
    }

    .price-perunit {
        color: $color-grey-extra-dark;
        font-size: 12px;
    }

    .deposit-price {
        color: $color-grey-extra-dark;
        font-size: 12px;
        padding-left: 5px;
    }

    .service-detail {
        position: relative;

        &__main__left {
            display: flex;
            align-items: center;
            width: 100%;
            flex-wrap: wrap;

            @media (min-width: 768px) {
                display: block;
            }
        }

        &__main__right__inner {
            position: relative;

            .product-favorite {
                opacity: 1;
                right: 0;
                left: auto;
                top: 3px;

                @media (max-width: 480px) {
                    top: 4px;
                }
            }

            .addratings {
                margin-top: 1px;
            }

            @include screen-lg {
                padding: 1.5em;

                box-shadow: $box-shadow;
                border-radius: 10px;
            }

            .watchdog-btn {
                margin-top: 1rem;

                button {
                    padding: 8px 20px 8px 20px;
                    width: 50%;
                }
            }
        }

        &__badges {
            > .row {
                flex-direction: column;

                @include screen-md {
                    flex-direction: row;
                }
            }
            .title {
                flex: 0 0 25px;

                @include screen-md {
                    flex: 0 0 80px;
                }
            }

            @media (max-width: 768px) {
                .product-badges {
                    top: 25px;
                    margin-bottom: 15px;
                }
            }
        }

        &__outStock {
            background: #fff3ed;
            color: #fd7328;
            text-align: center;
            padding: 9px;
            border-radius: 10px;
        }
    }

    .service-detail__main__right__wrapinner {
        position: relative;

        &.fixedTop {
            position: fixed;
        }
        &.fixedTopWithMenu {
            position: fixed;
        }

        .btn-variants {
            font-size: 14px;
            padding: 8px 25px;
            white-space: nowrap;
            min-width: 160px;
            max-width: 100%;
            font-weight: 500;

            @media (min-width: 480px) {
                width: 100%;
            }
        }
    }

    .block-availability {
        color: $color-green-dark;
        font-size: 13px;
        border-radius: 10px;
        margin-top: 10px;
        margin-bottom: 10px;
        line-height: 25px;
        background: $color-green-light;
        padding: 5px 1em;
        border: 1px solid $color-green-light;
        display: flex;
        justify-content: center;
        flex-direction: column;

        @media (min-width: 480px) {
            margin-bottom: 1em;
        }

        &--clickable {
            cursor: pointer;

            &:hover {
                @media (min-width: 1024px) {
                    border: 1px solid $color-green-dark;
                }
            }
        }

        &__fastest {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            flex-wrap: wrap;

            @include screen-sm {
                display: inline-flex;
            }

            @include screen-lg {
                display: flex;
            }
        }

        &__pilulka-logistics {
            display: flex;
            align-items: center;

            &::before {
                content: '';
                width: 13px;
                height: 13px;
                margin-right: 6px;
                display: block;
                background: $color-grey url('/nuxt-assets/icons/check-circle.svg') no-repeat center center;
                background-size: cover;
            }
        }

        .stock {
            white-space: nowrap;
        }
        &.noStock {
            color: $color-orange-dark;
            background: $color-orange-light;

            .block-availability__pilulka-logistics:before {
                background: #f6f6f6 url(/nuxt-assets/icons/check-circle-orange.svg) no-repeat 50%;
                fill: $color-orange-dark;
            }

            &:hover {
                @media (min-width: 1024px) {
                    border: 1px solid $color-orange-dark;
                }
            }

            .fastestdelivery-date {
                color: $color-orange-dark;
                flex-wrap: nowrap;
                display: flex;
            }
        }
        &:focus,
        &:focus-visible {
            outline: none;
        }

        a {
            color: $color-green-dark;
            text-decoration: underline;

            &:hover {
                color: $color-green-dark;
                text-decoration: none;
            }
        }

        .fastestdelivery-date {
            color: $color-green-dark;
            cursor: pointer;
            font-weight: 500;

            .delivery-term {
                white-space: nowrap;
                font-weight: normal;
            }

            &__link {
                text-decoration: underline;
            }
        }
    }

    .block-availability .text-success {
        color: $color-green-dark !important;
        font-weight: 500;
        text-align: right;
    }
}

@media (min-width: 480px) {
    .block-price {
        margin-bottom: 1.5em;
    }
}

@include screen-sm {
    .respo-wrapper .counter {
        display: block;
    }
}

.respo-wrapper .add-cart {
    border-radius: 50px;
}

@include media-breakpoint-down(sm) {
    .respo-wrapper {
        display: flex;
        justify-content: space-between;
        gap: 10px;

        &.add-wrapper-detail {
            align-items: center;
        }

        .add-cart__stock {
            text-align: center;
        }

        .add-cart__container {
            //max-width: 155px;
            flex: 1;
        }

        .add-cart {
            width: auto;
            margin-right: 0;
        }

        &.full {
            display: block;
            padding-bottom: 75px;

            .add-cart__container {
                margin-top: 10px;
                max-width: 100%;
            }
        }

        .popper {
            top: -117px;
        }
    }
}

@media (min-width: 992px) {
    .service-detail__sliders {
        margin-top: 1rem;
        grid-column: 1 / 3;
    }

    .header-sticky .inner-wrapper-sticky {
        margin-top: -115px;
        z-index: 99;
    }

    .service-detail__main__right.onlySticky {
        height: auto !important;
        position: relative !important;
        z-index: 1;

        .stickySidebarWrap {
            top: 135px !important;
            transform: none !important;
            position: sticky !important;
            transition: top 0.5s;
        }

        .inner-wrapper-sticky {
            position: static !important;
            transform: none !important;
        }
    }
    .header-sticky .service-detail__main__right.onlySticky .stickySidebarWrap,
    .header-sticky .pageWrap--with-headline-bar .service-detail__main__right.onlySticky .stickySidebarWrap {
        top: 20px !important;
    }
    .pageWrap--with-headline-bar .service-detail__main__right.onlySticky .stickySidebarWrap {
        top: 180px !important;
    }
}

.body-product-detail .menuFooterMobile {
    box-shadow: none;
}

.rc-rwp {
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    &__card {
        width: 100%;
    }

    &__card-three-quarter {
        width: 75%;
    }

    &__card-half {
        width: 50%;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        @media only screen and (max-width: 700px) {
            width: 100%;
        }
    }

    &__card-quarter {
        width: 25%;
    }
}

.rc-img {
    width: 100%;
    height: auto;
}

.rc-img-fit {
    max-width: 90% !important;
    width: fit-content;

    @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
        width: 60%;
    }

    height: auto;
    display: block;
    margin: auto;
}

@media (max-width: 700px) {
    .rc-img-stretched-mobile {
        max-width: 100% !important;
    }

    .rc-img-margin-mobile {
        margin-top: 1.5rem;
    }
}

.rc-block {
    h3,
    h2 {
        width: 100%;
    }

    &:not(.no-margin-bottom) {
        margin-bottom: 65px;
    }

    &.margin-top {
        margin-top: 40px !important;
    }
}

.textContent {
    p {
        font-size: 16px;
    }
}

.textContent .rc-block.rc-gift {
    display: none;
}

.rc-special-col {
    flex-wrap: wrap;
    align-self: baseline;
    display: flex;

    &__container {
        margin: 0.5rem 0;
    }

    * {
        text-align: center;
        color: $color-dark;
        width: 100%;
        font-size: 16px;
    }
    &__img {
        width: auto !important;
        height: fit-content !important;
        display: flex !important;
        align-items: center;
        margin: 0 auto 1.5rem;
        transition: all 0.2s ease;

        @media only screen and (max-width: 700px) {
            margin: 2rem auto 1.5rem;
        }

        img {
            display: block;
            margin: auto;
        }
    }

    &__img--effectsBorder {
        position: relative;

        &:before,
        &:after {
            @media only screen and (min-width: 750px) {
                box-sizing: border-box;
                transition: all 0.3s linear;
                border-radius: 50%;
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                border: 3px solid black;
                border-color: transparent black;
            }
        }

        &:hover {
            &:before {
                @media only screen and (min-width: 750px) {
                    transform: rotate(45deg);
                    border-width: 3px;
                }
            }

            &:after {
                @media only screen and (min-width: 750px) {
                    transform: rotate(-45deg);
                    border-width: 3px;
                }
            }
        }

        img {
            max-width: 100%;
            overflow: hidden;
            vertical-align: top;
            border-radius: 50%;
            padding: 10px;
        }
    }

    &__images--zoom:hover {
        @media only screen and (min-width: 750px) {
            transform: scale(1.2);
            transition: all 0.2s ease;
        }
    }

    &__content {
        align-self: flex-end;

        h3 {
            padding-bottom: 0 !important;
            color: #000 !important;
        }

        p {
            margin-top: 1rem;
        }
    }

    h3 {
        font-size: 28px;
    }

    &.rc-rwp__card-half {
        width: 45%;
    }

    &.rc-rwp__card-third {
        width: 28%;

        h3 {
            font-size: 20px;
        }

        @media only screen and (max-width: 600px) {
            width: 100% !important;

            h3 {
                font-size: 28px;
            }
        }
    }

    &.rc-rwp__card-quarter {
        width: 21%;

        h3 {
            font-size: 16px;
        }

        @media only screen and (max-width: 700px) {
            width: 45% !important;

            h3 {
                font-size: 20px;
            }
        }

        @media only screen and (max-width: 500px) {
            width: 100% !important;
        }
    }

    &[class^='rc-rwp__card-'] {
        margin: 0 auto;
        @media only screen and (max-width: 500px) {
            width: 100%;

            h3 {
                font-size: 20px;
            }
        }
    }
}

@media (max-width: 768px) {
    .add-wrapper-detail {
        position: fixed;
        bottom: 0;
        width: 100%;
        left: 0;
        background: #fff4f2;
        padding: 10px 25px 85px;
        box-shadow: 0px -4px 4px -2px rgba(0, 0, 0, 0.08);
        z-index: 299;
    }
}

.product-badges {
    position: relative;
    bottom: 0;
    flex-direction: row;
    padding: 0;
    flex-wrap: wrap;
    width: auto;
    justify-content: left;
    display: flex;

    .item {
        font-size: 12px;
        background-color: rgba(255, 255, 255, 0.9);
        border-radius: 25px;
        padding: 3px 10px 3px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: auto;
        font-weight: 500;
        margin-right: 1em;
        box-shadow: none;
        margin-bottom: 1em;
        line-height: inherit;
        display: flex;
        align-items: center;
        gap: 5px;

        &.certificates {
            background: #fff;
            box-shadow: 0 0 20px -6px rgba(0, 0, 0, 0.22);
            border-radius: 1000px;

            > img {
                max-width: 16px;
                margin-right: 5px;
            }
        }
    }
}

.monthly-packages {
    gap: 7px;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin-bottom: 15px;

    &__title {
        margin: 15px 0 7px;
        font-size: 15px;
        font-weight: 600;
    }

    &__item {
        border: 1px solid $color-grey-dark;
        border-radius: 10px;
        padding: 15px 15px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        &:hover {
            border: 1px solid $color-cta;
        }

        s {
            color: $color-grey-semi-dark;
            padding-right: 10px;
            font-size: 14px;
        }
    }

    &__visual-tag {
        background-color: rgb(58, 88, 37);
        color: rgb(255, 255, 255);
        border-radius: 25px;
        display: inline-block;
        font-size: 11px;
        font-weight: 500;
        line-height: 14px;
        padding: 3px 10px;
        margin-top: 7px;
    }
}

.color-grey {
    color: $color-grey-extra-dark;
    font-size: 14px;
}
</style>
